// RECONÈIXER UN ARQUETIP EN UN DIBUIX.
//
// Fora de React, amb dos imports que tampoc no en tenen. Es prova sol:
// `npm run prova:reconeix`.
//
// ── QUÈ ES COMPARA, I PER QUÈ NO N'HI HA PROU AMB COMPTAR BUCLES ────────────────────────────
//
// La temptació és comptar-los: «un R i un B» → límits al creixement. **I està malament**, perquè
// «solucions que apedacen» també és un R i un B. Amb dos arquetips ja falla.
//
// El que els distingeix és **com estan fets i com se solapen**:
//
//     límits al creixement   R de 2 · B de 3 · comparteixen 1 variable
//     solucions que apedacen B de 2 · R de 3 · comparteixen 2 variables
//
// Per això l'empremta d'un conjunt de bucles és **la mena i la llargada de cadascun, més quantes
// variables comparteixen dos a dos**. Això sí que els separa, i és el mateix que fa qui reconeix
// un arquetip a ull: no compta bucles, mira com encaixen.
//
// ── I ES BUSCA A DINS, NO A SOBRE ───────────────────────────────────────────────────────────
//
// Un dibuix de debò és més gran que l'arquetip: hi ha variables de més i sovint bucles de més.
// Comparar el dibuix sencer amb l'arquetip sencer no encertaria gairebé mai. El que es mira és si
// **entre els bucles del dibuix n'hi ha uns quants que fan l'empremta de l'arquetip**.
//
// ── EL QUE AIXÒ NO ÉS ───────────────────────────────────────────────────────────────────────
//
// **No és una etiqueta, és una pista.** Dues estructures poden encaixar i voler dir coses
// diferents: l'empremta mira la forma i no els noms ni el que significa cada variable. Per això
// el que es diu a la pantalla és «s'assembla a», mai «això és», i **no s'hi posa cap número de
// confiança**: un percentatge inventat només serviria per fer semblar exacte el que no ho és.

import { detectaBucles } from './buclesCausals';
import { ARQUETIPS } from './arquetips';

// Quants bucles com a màxim es miren. Amb els arquetips de tres bucles, provar totes les
// combinacions de dotze ja són dues-centes vint proves: prou per a un diagrama d'aula i lluny de
// penjar res.
const MAX_BUCLES = 12;

// L'empremta: la mena i la llargada de cada bucle, i quantes variables comparteixen dos a dos.
// **Ordenada**, perquè dos conjunts iguals han de donar la mateixa cadena vinguin com vinguin.
export function empremta(bucles) {
  const cadascun = bucles.map((b) => `${b.type[0]}${b.path.length}`);
  const ordre = cadascun.map((x, i) => [x, i]).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  const solapaments = [];
  for (let i = 0; i < ordre.length; i++) {
    for (let j = i + 1; j < ordre.length; j++) {
      const a = new Set(bucles[ordre[i][1]].path);
      const comuns = bucles[ordre[j][1]].path.filter((x) => a.has(x)).length;
      solapaments.push(comuns);
    }
  }
  return `${ordre.map((x) => x[0]).join(',')}|${solapaments.sort().join(',')}`;
}

const buclesDe = (variables, relacions) => detectaBucles(
  variables.map((v) => ({ id: String(v.id) })),
  relacions.map((r) => ({ id: String(r.id), source: String(r.source), target: String(r.target),
                          polarity: r.polarity })),
);

// L'empremta de cada arquetip, calculada un sol cop a partir de la seva pròpia estructura. **No
// s'escriu a mà enlloc**: si algun dia es canvia un arquetip, l'empremta el segueix sola.
const EMPREMTES = ARQUETIPS.map((a) => {
  const bucles = buclesDe(
    a.variables.map((v) => ({ id: v.id })),
    a.relacions.map((r, i) => ({ id: `r${i}`, source: r.from, target: r.to, polarity: r.polarity })),
  );
  return { id: a.id, n: bucles.length, empremta: empremta(bucles) };
}).filter((x) => x.n > 0 && x.n <= 3);

// Totes les combinacions de `k` elements. Sense recursió profunda i amb el topall a sobre.
function combinacions(llista, k) {
  const fora = [];
  const anar = (des, acum) => {
    if (acum.length === k) { fora.push([...acum]); return; }
    for (let i = des; i < llista.length; i++) { acum.push(llista[i]); anar(i + 1, acum); acum.pop(); }
  };
  anar(0, []);
  return fora;
}

// Els arquetips que el dibuix conté. Retorna, per a cadascun, **quins bucles hi encaixen**: dir
// «s'assembla a desplaçar la càrrega» sense dir per on seria demanar que se't cregui.
export function reconeix(variables, relacions) {
  if (!variables?.length || !relacions?.length) return [];
  const bucles = buclesDe(variables, relacions).slice(0, MAX_BUCLES);
  if (!bucles.length) return [];

  const trobats = [];
  for (const e of EMPREMTES) {
    if (e.n > bucles.length) continue;
    // ELS ARQUETIPS D'UN SOL BUCLE NOMÉS VALEN EN UN DIBUIX PETIT.
    //
    // «Equilibri amb retard» és un equilibrador de tres variables, i **això és a dins de gairebé
    // tot**: també és a dins de «límits al creixement», on és cert però no és el que s'hi ha de
    // veure. I un reforçador de quatre és a dins de «desplaçar la càrrega», on ja no és ni cert
    // que sigui una escalada.
    //
    // Dir-los sempre convertiria la pista en soroll, i una pista que surt sempre s'aprèn a
    // ignorar. Per això un arquetip d'un sol bucle només es diu **quan el dibuix és això i prou**,
    // no quan hi cap a dins. És estricte a posta: val més callar que dir un nom que no toca.
    if (e.n === 1 && bucles.length > 1) continue;
    for (const grup of combinacions(bucles, e.n)) {
      if (empremta(grup) !== e.empremta) continue;
      trobats.push({ id: e.id, bucles: grup.map((b) => ({ type: b.type, path: b.path })) });
      break;   // un cop per arquetip: dir-lo tres vegades no diu res de nou
    }
  }
  // Els que fan servir més bucles primer: encaixar tres coses és més informatiu que encaixar-ne
  // una, i el més probable és que sigui el que qui dibuixa buscava.
  return trobats.sort((a, b) => b.bucles.length - a.bucles.length);
}

const RECONEIX = { empremta, reconeix, MAX_BUCLES };
export default RECONEIX;
